import React from 'react';
import { useTranslation } from 'react-i18next';
import BaseScadaNode from './BaseScadaNode';

export function StripFolderSVG({ config = {}, width = 100, height = 90, liveValues = {} }) {
    const running = parseFloat(liveValues.state ?? 0) >= parseFloat(config.threshold ?? 0.5);
    const hasError = liveValues.error === 1 || liveValues.error === true ||
        liveValues.error === '1' || liveValues.error === 'true';
    const statusColor = hasError ? '#EF4444' : running ? '#22C55E' : '#64748B';

    const ristrasPerCarton = parseInt(config.ristras_per_carton ?? 6);
    const currentRistras = parseInt(liveValues.ristras_count ?? 0);
    const fillPct = Math.min(1, currentRistras / Math.max(1, ristrasPerCarton));
    const count = parseInt(liveValues.count ?? 0);
    const lanes = parseInt(config.lanes_envasadora ?? 6);
    const halfLanes = Math.floor(lanes / 2);

    const bodyX = 4;
    const bodyY = 4;
    const bodyW = width - 8;
    const bodyH = height - 8;
    const cx = width / 2;
    const cy = height / 2;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Machine body */}
            <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={4}
                fill="#1E293B" stroke={statusColor} strokeWidth={2} />

            {/* --- Top-left: Incoming ristras (sachets strips) --- */}
            <g transform={`translate(8, ${bodyY + 6})`}>
                {/* Show half-lane pairs as wavy lines representing ristra strips */}
                {Array.from({ length: Math.min(halfLanes, 4) }, (_, i) => {
                    const y = i * 6;
                    return (
                        <g key={i}>
                            <path d={`M 0,${y} Q 5,${y - 2} 10,${y} Q 15,${y + 2} 20,${y} L 26,${y}`}
                                fill="none" stroke={running ? '#22C55E' : '#64748B'}
                                strokeWidth={2} opacity={0.7}>
                                {running && (
                                    <animate attributeName="stroke-dashoffset"
                                        from="20" to="0" dur="0.8s" repeatCount="indefinite" />
                                )}
                            </path>
                            {/* Small sachet marks */}
                            {[6, 12, 18].map(sx => (
                                <line key={sx} x1={sx} y1={y - 2} x2={sx} y2={y + 2}
                                    stroke={running ? '#22C55E' : '#64748B'} strokeWidth={0.8} opacity={0.5} />
                            ))}
                        </g>
                    );
                })}
                {/* Lane count label */}
                <text x={28} y={-1} fontSize={7} fill="#64748B" fontFamily="monospace">
                    {halfLanes}+{halfLanes}
                </text>
            </g>

            {/* --- Bottom-left: Incoming empty carton --- */}
            <g transform={`translate(8, ${cy + 8})`}>
                <rect x={0} y={0} width={18} height={14} rx={1}
                    fill="none" stroke="#94A3B8" strokeWidth={1.2} strokeDasharray="3 2" />
                <line x1={0} y1={3} x2={18} y2={3}
                    stroke="#94A3B8" strokeWidth={0.8} opacity={0.5} />
                {/* Arrow into machine */}
                <path d="M 20,7 L 26,7" stroke="#94A3B8" strokeWidth={1} markerEnd="url(#arrowSF)" />
            </g>

            {/* --- Center: Folding mechanism --- */}
            <g transform={`translate(${cx - 8}, ${cy - 10})`} opacity={running ? 1 : 0.4}>
                {/* Fold-down arrow (top half onto bottom half) */}
                <path d="M 0,-4 C 4,-8 12,-8 16,-4" fill="none" stroke={statusColor} strokeWidth={1.5} />
                <path d="M 0,24 C 4,28 12,28 16,24" fill="none" stroke={statusColor} strokeWidth={1.5} />
                {/* Center merge arrow */}
                <path d="M 8,-6 L 8,0 M 8,20 L 8,26" stroke={statusColor} strokeWidth={1.2}
                    markerEnd="url(#arrowSF)" />
                {running && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                )}
            </g>

            {/* --- Right: Filled carton output --- */}
            <g transform={`translate(${width - 32}, ${cy - 10})`}>
                {/* Box body */}
                <rect x={0} y={4} width={20} height={16} fill="#334155" stroke={statusColor} strokeWidth={1.2} />
                {/* Ristras inside box (fill indicator) */}
                {fillPct > 0 && (
                    <rect x={1} y={4 + 16 * (1 - fillPct)} width={18}
                        height={Math.max(1, 16 * fillPct)}
                        fill={statusColor} opacity={0.25} />
                )}
                {/* Ristra lines inside */}
                {Array.from({ length: Math.min(currentRistras, ristrasPerCarton) }, (_, i) => {
                    const ry = 4 + 16 - (i + 1) * (16 / ristrasPerCarton);
                    if (ry < 4) return null;
                    return (
                        <line key={i} x1={3} y1={ry} x2={17} y2={ry}
                            stroke={statusColor} strokeWidth={1} opacity={0.6} />
                    );
                })}
                {/* Top flap closed when full */}
                {fillPct >= 1 && (
                    <rect x={0} y={0} width={20} height={4} fill="#475569" stroke={statusColor} strokeWidth={1} />
                )}
            </g>

            {/* Arrow marker */}
            <defs>
                <marker id="arrowSF" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                    <path d="M 0,0 L 6,2 L 0,4 Z" fill={statusColor} />
                </marker>
            </defs>

            {/* Progress bar (bottom) */}
            <rect x={bodyX + 4} y={height - 12} width={bodyW - 8} height={4} rx={2}
                fill="#0F172A" stroke="#334155" strokeWidth={0.5} />
            <rect x={bodyX + 4} y={height - 12}
                width={Math.max(0, (bodyW - 8) * fillPct)} height={4} rx={2}
                fill={statusColor} opacity={0.7} />

            {/* Count and progress text */}
            <text x={cx} y={height - 14} textAnchor="middle"
                fill="#94A3B8" fontSize={8} fontFamily="monospace">
                {currentRistras}/{ristrasPerCarton} {count > 0 ? `(${count})` : ''}
            </text>

            {/* Error flash */}
            {hasError && (
                <rect x={2} y={2} width={width - 4} height={height - 4} rx={4}
                    fill="none" stroke="#EF4444" strokeWidth={2}>
                    <animate attributeName="stroke-opacity" values="1;0;1" dur="0.6s" repeatCount="indefinite" />
                </rect>
            )}
        </svg>
    );
}

export default function StripFolderNode(props) {
    const { data } = props;
    const { t } = useTranslation();
    const liveValues = data?.liveValues || {};
    const threshold = parseFloat(data?.config?.threshold ?? 0.5);
    const running = parseFloat(liveValues.state ?? 0) >= threshold;
    const requestingRistras = liveValues.requesting_ristras != null
        && parseFloat(liveValues.requesting_ristras) >= threshold;
    const requestingCarton = liveValues.requesting_carton != null
        && parseFloat(liveValues.requesting_carton) >= threshold;
    const w = data?.width || 100;
    const h = data?.height || 90;

    return (
        <BaseScadaNode {...props}>
            <StripFolderSVG
                config={data?.config}
                width={w} height={h}
                liveValues={liveValues}
            />
            {running && requestingRistras && (
                <div style={{ position: 'absolute', top: 4, left: 4, right: 4, background: '#7F1D1D',
                    border: '1px solid #EF4444', borderRadius: 4, padding: '2px 4px',
                    fontSize: 9, color: '#FCA5A5', textAlign: 'center', fontWeight: 600,
                    pointerEvents: 'none', zIndex: 5 }}>
                    {t('scada.noRistras', 'Sin ristras')}
                </div>
            )}
            {running && requestingCarton && !requestingRistras && (
                <div style={{ position: 'absolute', top: 4, left: 4, right: 4, background: '#92400E',
                    border: '1px solid #F59E0B', borderRadius: 4, padding: '2px 4px',
                    fontSize: 9, color: '#FEF3C7', textAlign: 'center', fontWeight: 600,
                    pointerEvents: 'none', zIndex: 5 }}>
                    {t('scada.noCarton', 'Sin carton')}
                </div>
            )}
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', overflow: 'hidden',
                    whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
